<template>
    <div class="subscription-homepage">
		<div class="trial-panel">
			<div class="logo-block">
				<a class="logo-link" v-show="!user">
					<rt-picture src="/assets/app/class/icon-logo-white.svg"/>
				</a>
				<rt-picture class="icon-flag" src="/assets/app/class/icon-flag.svg"/>
			</div>
			<rt-picture class="icon-confetti" src="/assets/app/class/icon-confetti.svg" v-if="!tableOptions.banner_image_url"/>
			<div class="header-block" v-if="tableOptions.show_title">
				<h2 class="header-title" v-if="!yearly">{{tableOptions.title}}</h2>
				<h2 class="header-title" v-else>Get ReadTheory for your school</h2>
			</div>
			<div v-if="tableOptions.banner_image_url" class="pricing-banner-wrapper" :class="{ 'no-subtitle': !tableOptions.sub_title }">
				<img class="pricing-custom-banner" :src="tableOptions.banner_image_url">
			</div>
			<p class="header-description" v-if="!yearly">{{tableOptions.sub_title}}</p>
			<p class="header-description" v-else>Includes all our advanced features for schools, teachers, and students</p>
		</div>
		<div class="video-block">
      <subscription-plan-view
        class="subscription-view"
        :class="{ 'has-banner': tableOptions.banner_image_url, 'without-title': tableOptions.banner_image_url && !tableOptions.show_title }"
        :pricingTable="true"
        @freePlanSelected="handleSelectFreePlan"
        @pricingChanged="handleChangePlan"
        @onGetQuote="showQuoteModal"
        v-if="!yearly"
      />
			<yearly-subscription-plan-view class="subscription-view" :class="{ 'has-banner': tableOptions.banner_image_url, 'without-title': tableOptions.banner_image_url && !tableOptions.show_title }" :pricingTable="true" @freePlanSelected="handleSelectFreePlan" @pricingChanged="handleChangePlan" v-else />
      <subscription-lesson-plan
        class="lesson-block"
        :pricingName="pricingRoleName"
        @onShowPlan="gotoPlanSection"
        v-if="tableOptions.feature_highlight_widget"
      />
      <subscription-satisfaction
        class="guarante-block"
        v-else
      />
			<div class="premium-video-block flex-wrapper">
				<div class="video-title">Curious about the perks of Pro?</div>
        <div class="video-subtitle">See the difference in the quick video</div>
        <div class="video-image">
          <img class="video-bg" src="/assets/app/signup/video-slide.png">
          <primary-button class="video-btn" @click="isVideoVisible = true">
            <rt-picture src="/assets/app/signup/icon_play.svg" class="video-btn-icon"></rt-picture>
            Watch Video
          </primary-button>
        </div>
			</div>
		</div>
		<div class="review-block">
			<h2 class="review-title">Reviews from happy users</h2>
			<div class="carousel-wrapper">
				<primary-button class="btn-prev btn-normal" @click="handleClickPrev">
					<rt-picture src="/assets/app/class/icon-arrow-left.svg"></rt-picture>
				</primary-button>
				<carousel :per-page="1" :navigate-to="navigate" :mouse-drag="false" :speed="1000" :paginationEnabled="false">
					<slide :key="idx" v-for="(reviewerItem, idx) in reviewers">
					<SubscriptionReviewCard :reviewer="reviewerItem" />
					</slide>
				</carousel>
				<primary-button class="btn-next btn-normal" @click="handleClickNext">
					<rt-picture src="/assets/app/class/icon-arrow-right-blue.svg"></rt-picture>
				</primary-button>
			</div>
			<div class="slide-avatar-wrapper">
				<img @click="navigate = idx" :key="idx" v-for="(reviewerItem, idx) in reviewers" class="avatar-icon" :class="{inactive: navigate !== idx}" :src="reviewerItem.icon">
			</div>
		</div>
		<div class="features-block">
      <div class="feature-block-title">
        <div class="title">Get more power to deeply engage students in reading.</div>
        <div class="sub-title">Explore the <span>Premium</span> ReadTheory Experience</div>
      </div>
      <div class="feature-details" v-for="(detail, index) in featureDetails" :key="index">
        <img class="feature-image" :src="detail.img">
        <div class="feature-description">
          <h3 class="feature-title">{{detail.title}}</h3>
          <p class="feature-text">{{detail.text}}</p>
          <primary-button @click="gotoPlanSection" class="feature-btn">Choose your plan</primary-button>
        </div>
      </div>
    </div>
		<SubscriptionFooter :withOption="true"/>
		<SubscriptionVideoModal @close="isVideoVisible = false" @onShowPlan="gotoPlanSection" :show="isVideoVisible"/>
    <QuoteRequestModal
      :show="isQuoteModalOpened"
      @onClose="closeQuoteModal"
    />
    </div>
</template>

<script>
  import { getUser } from 'common/UserService';
  import { Carousel, Slide } from 'vue-carousel';
  import SubscriptionPlanView from './SubscriptionPlanView.vue';
  import SubscriptionSatisfaction from './subscription/SubscriptionSatisfaction.vue';
  import SubscriptionLessonPlan from './subscription/SubscriptionLessonPlan.vue';
  import SubscriptionReviewCard from './subscription/SubscriptionReviewCard.vue';
  import SubscriptionPremiumPanel from './subscription/SubscriptionPremiumPanel.vue';
  import SubscriptionFQAPanel from './subscription/SubscriptionFQAPanel.vue';
  import SubscriptionFooter from './subscription/SubscriptionFooter.vue';
  import SubscriptionVideoModal from './subscription/SubscriptionVideoModal.vue';
  import PrimaryButton from '../common/PrimaryButton.vue';
  import RtPicture from '../common/Picture.vue';
  import QuoteRequestModal from '../quote/QuoteRequestModal.vue';
  import { SUBSCRIPTION_REVIEWERS, BASE_PATH } from 'config';
  import YearlySubscriptionPlanView from './YearlySubscriptionPlanView.vue';
  import { loadCustomerlyForLeads, toggleCustomerly } from '../../../teachers/customerly';
  import {SCHOOLS_FAQ_LIST,FAQ_LIST} from '../../../config';
  import {table_options, PRICING_ROLE_NAME} from '../../../common/plans';

    export default {
	props: ['showInSignup'],
    data: function() {
      return {
        user: null,
        reviewers: SUBSCRIPTION_REVIEWERS,
        navigate: 0,
        yearly: false,
        tableOptions: {},
        isVideoVisible: false,
        isQuoteModalOpened: false,
        pricingRoleName: PRICING_ROLE_NAME,
        featureDetails: [
          {
            img: '/assets/app/signup/feature_1.png',
            title: 'Get the data insights you need to drive instruction',
            text: 'Know where every student stands according to ELA Curriculum Standards, Lexile Level, and Grade Level. Easily export data and share reports with your school community.'
          },
          {
            img: '/assets/app/signup/feature_2.png',
            title: 'Instantly see who\'s on task and who needs a nudge.',
            text: 'Monitor student productivity effortlessly with the Last Seen Indicator, designed to give you real-time insights into student engagement.'
          },
          {
            img: '/assets/app/signup/feature_3.png',
            title: 'Evaluate students’ skills by assigning formative assessments.',
            text: 'Give students a clean slate by assigning a Retake Pretest Assessment, perfect for when you need a refreshed review of their skills. Whether it’s the start of a new term or a shift in learning needs, you\'ll always know where they stand.'
          },
          {
            img: '/assets/app/signup/feature_4.png',
            title: 'Set a reading routine that sticks.',
            text: 'Practice makes perfect! With recurring activities and point goals, you can set a weekly reading routine that gives students regular practice. Easily check progress in real-time to see students’ performance!'
          },
          {
            img: '/assets/app/signup/feature_5.png',
            title: 'Seamlessly align ReadTheory with your existing curriculum.',
            text: 'With Premium you can use ReadTheory for a whole-class lesson by assigning a specific passage from the library! Allow students to work in groups, take a common assessment, or hold a class discussion on it.'
          },
          {
            img: '/assets/app/signup/feature_6.png',
            title: 'Motivate students to reach their full potential.',
            text: 'With Premium you can deeply engage students with classroom competitions, badges, prizes, and more! Bonus - students can track their own progress and have agency over their achievements. '
          },
          {
            img: '/assets/app/signup/feature_7.png',
            title: 'Gain more control and focus for your students.',
            text: 'Create a distraction-free experience for students by turning off ads. Enjoy more custom-settings like easily adjusting the minimum and maximum grade levels when using auto-assignments.'
          },
          {
            img: '/assets/app/signup/feature_8.png',
            title: 'Get the support you need, when you need it.',
            text: 'Your success is our success. Priority support means your support inquiries will always get answered first. Look at us as an extension of your teaching team!'
          }
        ]
      }
    },
    components: {
      YearlySubscriptionPlanView,
      Carousel,
      Slide,
      SubscriptionPlanView,
      SubscriptionSatisfaction,
      SubscriptionLessonPlan,
      SubscriptionReviewCard,
      SubscriptionPremiumPanel,
      SubscriptionFQAPanel,
	  SubscriptionFooter,
	  SubscriptionVideoModal,
      PrimaryButton,
      RtPicture,
	  QuoteRequestModal
    },
    computed: {
      show() {
        return true
        //return this.user ? true : false;
      },
      isSchool() {
        return this.$route.params.role?.startsWith('school')
      },
      faqItems() {
        return this.isSchool ? SCHOOLS_FAQ_LIST : FAQ_LIST
      }
    },
    mounted() {
      this.user = getUser();
      if (this.isSchool) {
        this.yearly = true;
	  }
	  this.tableOptions = table_options;
      loadCustomerlyForLeads()
    },
    methods: {
	  subscriptionIcon(subscription) {
		switch (subscription.name) {
		  case 'PRO': return '/assets/app/class/icon-customer-group.svg';
		  case 'PRO PLUS': return '/assets/app/class/icon-participants.svg';
		}
	  },
      handleClickPrev() {
        if (this.navigate > 0) {
          this.navigate = this.navigate - 1;
        } else {
          this.navigate = this.reviewers.length - 1;
        }
      },
      handleClickNext() {
        if (this.navigate < this.reviewers.length) {
          this.navigate = this.navigate + 1;
        } else {
          this.navigate = 0;
        }
	  },
	  gotoPlanSection() {
		  this.isVideoVisible = false;
		  $("html, body").animate({ scrollTop: 81 }, "slow");
	  },
	  handleChangePlan() {
		this.$emit('onPricingChanged');
	  },
	  handleSelectFreePlan() {
		if (!this.showInSignup) {
		  history.pushState(null, 'My Classes', BASE_PATH+'/welcome/userHome');
		  location.reload();
		} else {
		  this.handleChangePlan();
		}
	  },
    showQuoteModal() {
      this.isQuoteModalOpened = false;
      toggleCustomerly(false);
      window.open('/get-in-touch?utm_source=in_app&utm_medium=pricing_table&utm_campaign=quote', '_blank');
    },
    closeQuoteModal() {
      this.isQuoteModalOpened = false;
      toggleCustomerly(true);
    }
    }
  }
</script>

<style scoped lang="scss">
  @import '../common/variables';

  .permission-warnning {
    padding: 10px;
  }
  .subscription-homepage {
	background: $light-blue;
	p {
	  text-align: left;
	}
    .trial-panel {
	  width: 100%;
	  height: 513px;
	  background: #132C55;
	  padding: 0 135px;
      position: relative;
	  .logo-block {
		padding-top: $pricing-page-logo-padding-top;
		text-align: left;
	  }
	  .icon-flag {
		position: absolute;
		top: 0;
		left: 50%;
		transform: translateX(-50%);
	  }
	  .icon-confetti {
		margin-top: $pricing-page-confetti-margin-top;
	  }
	  .header-block {
		margin-top: 12px;
		.header-title {
		  font-family: $title-font;
		  font-weight: 700;
		  font-size: 36px;
		  line-height: 1;
		  color: #fff;
		}
	  }
	  .pricing-banner-wrapper {
		margin-top: 16px;
		&.no-subtitle {
		  margin-bottom: 10px;
		}
		.pricing-custom-banner {
		  max-width: 100%;
		  height: auto;
		}
	  }
	  .header-description {
		padding-top: 16px;
		text-align: center;
		font-size: 16px;
		line-height: 1;
		color: #fff;
	  }
	}
	
	.subscription-view {
	  background: transparent;
      margin: 0 auto -230px;
	  transform: translateY(-230px);
	  &.has-banner {
		margin-bottom: -$pricing-table-transform-height-medium;
		transform: translateY(-$pricing-table-transform-height-medium);
	  }
	  &.without-title {
		margin-bottom: -$pricing-table-transform-height-small-t;
		transform: translateY(-$pricing-table-transform-height-small-t);
	  }
	}
	.video-block {
	  background: #fff;
	  .subscription-block {
		display: flex;
		width: fit-content;
		margin: 0 auto -230px;
		transform: translateY(-230px);
		
		.subscription-card {
		  max-width: 362px;
		  background: #FFF;
		  box-shadow: 0px 4px 32px rgba(0, 0, 0, 0.08);
		  padding: 40px 32px;
		  margin-right: 30px;
		  border-radius: 22px;
		  .card-name-wrapper {
			justify-content: center;
			.pricing-icon {
			  margin-right: 8px;
			}
			.pricing-name {
			  font-weight: 700;
			  font-size: 16px;
			  color: $title-grey;
			}
		  }
		  .card-price-wrapper {
			justify-content: center;
			margin-top: 30px;
			line-height: 1;
			.pricing-currency {
			  margin-right: 11px;
			  font-size: 18px;
			  color: $light-blue2;
			}
			.pricing-value {
			  margin-right: 11px;
			  font-family: $title-font;
			  font-size: 56px;
			  color: $light-blue2;
			}
			.pricing-date {
			  font-size: 18px;
			  color: $light-blue2;
			}
		  }
		  &:last-child {
			margin: 0;
		  }
		  .card-description {
			margin-top: 8px;
			text-align: center;
			font-weight: 700;
			font-size: 18px;
			color: $title-dark-grey;
		  }
		  .card-button {
			width: 100%;
			background: #EDF3FE;
			padding: 17px 0;
			margin-top: 16px;
			margin-bottom: 0;
			border: 0;
			border-radius: 30px;
			font-weight: 700;
			font-size: 14px;
			color: $light-blue2;
		  }
		  .card-features {
			margin-top: 40px;
			.features-description {
			  text-align: left;
			  font-weight: 700;
			  font-size: 14px;
			  color: $title-dark-grey;
			}
			.features-list {
			  list-style: none;
			  .feature-item {
				align-items: flex-start;
				justify-content: flex-start;
				margin-top: 25px;
				.rt-picture {
				  margin-right: 12px;
				}
				.feature-description {
				  line-height: 1;
				  text-align: left;
				  font-size: 14px;
				  color: $title-grey;
				}
			  }
			}
		  }
		}
	  }
	}

  .lesson-block {
    max-width: 1170px;
    margin: 30px auto 0;
  }

  .guarante-block {
    background: $blue-100;
    max-width: 754px;
    margin: 20px auto 0;
    padding: 24px 0;
    border-radius: 22px;
  }

	.premium-video-block {
	  max-width: 1043px;
	  padding: 96px 0;
	  margin: 0 auto;
    flex-direction: column;
    .video-title {
      font-family: $title-font;
      font-weight: 700;
      font-size: 36px;
      color: $title-dark-grey;
    }
    .video-subtitle {
      font-size: 16px;
      line-height: 16px;
      color: $title-grey;
    }
    .video-image {
      margin-top: 16px;
      position: relative;
      .video-bg {
        max-width: 80%;
      }
      .video-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 200px;
        height: 46px;
        margin-top: -23px;
        margin-left: -100px;
        background-color: $light-red4;
        display: flex;
        justify-content: center;
        .video-btn-icon {
          margin-right: 5px;
        }
      }
    }

	}
	::v-deep(.plan-view-main) {
	  .subscription-card {
		width: 370px;
	  }
  	}
	.review-block {
	  padding: 96px 235px;
	  .review-title {
		font-family: $title-font;
		font-weight: 700;
		font-size: 36px;
		color: $title-dark-grey;
	  }
	  .carousel-wrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: 870px;
		margin: 48px auto 0;
	    .VueCarousel {
		  width: 780px;
		  ::v-deep(.subscription-review-card) {
			width: 780px;
			margin-right: 10px;
		  }
		}
	  }
	  .slide-avatar-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-top: 28px;
		.avatar-icon {
	  	  margin-right: 40px;
		  &:last-child {
			margin: 0;
		  }
		  &.inactive {
			opacity: 0.22;
		  }
		}
	  }
	}
	.reading-block {
	  background: #fff;
	  ::v-deep(.white-block) {
		max-width: 1200px;
		margin: 0 auto;
	  }
	}
    .features-block {
        display: flex;
        flex-direction: column;
        background-color: #fff;
        padding: 2rem;
        .feature-block-title {
          .title {
            font-family: $title-font;
            font-weight: 700;
            font-size: 36px;
            color: $title-dark-grey;
          }
          .sub-title {
            font-size: 16px;
            color: $title-grey;
            span {
              font-weight: 700;
              color: $light-blue2;
            }
          }
        }
        .feature-details {
          display: flex;
          max-width: 1144px;
          margin: 30px auto;
          gap: 30px;
          &:nth-child(even) {
            flex-direction: row-reverse;
          }

          .feature-image {
            width: 50%;
            height: 100%;
          }

          .feature-description {
            text-align: left;
            margin: auto;

            .feature-title {
              font-family: $title-font;
              font-weight: 700;
              font-size: 24px;
              color: $title-dark-grey;
            }

            .feature-text {
              font-size: 16px;
              line-height: 24px;
              color: $title-grey;
            }

            .feature-btn {
              margin-top: 26px;
              border: none;
            }
          }
        }

      }

    @media(max-width: 1170px) {
	  .trial-panel {
		padding: 0 20px;
	  }
	  ::v-deep(.main-container) {
		padding-left: 10px;
		padding-right: 10px;
	  }
      .video-block {
        padding-left: 10px;
        padding-right: 10px;
        ::v-deep(.plan-view-main) {
          flex-wrap: wrap;
          .subscription-card {
            margin-top: 20px;
            &:nth-child(2n+2) {
              margin-right: 0;
            }
            &:first-child, &:nth-child(2) {
              margin-top: 0;
            }
          }
        }
		.premium-video-block {
		  flex-direction: column;
		  .video-content {
			margin-top: 16px;
		  }
		}
      }
    }
    @media(max-width: 1040px) {
	  .review-block {
		padding-left: 0;
    	padding-right: 0;
		.carousel-wrapper {
          padding: 0 10px;;
		  .VueCarousel {
			width: 80%;
			margin: 0 auto;
			  ::v-deep(.VueCarousel-inner) {
			    flex-basis: 100%;
			  }
			  ::v-deep(.subscription-review-card) {
			    width: 100%;
			  }
		  }
        }
	  }
    }
    @media(max-width: 900px) {
      .premium-features-wrapper {
        display: block;
      }
      .trial-banner-wrapper {
        padding: 24px 16px;
      }
      .guarante-block {
        padding-left: 10px;
        padding-right: 10px;
      }
    }
    @media(max-width: 767px) {
	  .trial-panel {
		padding: 0 $pricing-page-mobile-padding-lr;
	  }
      .premium-features-wrapper {
        padding: 50px 0;
        .video-frame-wrapper {
          min-width: auto;
          width: 80%;
          margin: 0;
          .video-wrapper {
            width: 100%;
          }
        }
      }
      .carousel-wrapper {
        padding: 0;
      }
      .video-block {
        ::v-deep(.plan-view-main) {
          .subscription-card {
            margin-top: 20px;
            margin-right: 0;
            &:nth-child(2) {
              margin-top: 20px;
            }
          }
        }
		.premium-video-block {
		  .video-bg {
		    margin: 0;
		  }
	    }
      }
	  ::v-deep(.main-container) {
	    flex-direction: column;  
	  }
	}
	  @media(max-width: 586px) {
	  .video-block {
		.subscription-view {
		  margin-bottom: -$pricing-table-transform-height-medium;
		  transform: translateY(-$pricing-table-transform-height-medium);
		  &.has-banner {
			margin-bottom: -$pricing-table-transform-height-medium-t;
		    transform: translateY(-$pricing-table-transform-height-medium-t);
		  }
		  &.without-title {
		   margin-bottom: -$pricing-table-transform-height-medium-t;
		   transform: translateY(-$pricing-table-transform-height-medium-t);
		  }
		}
	  }
      .feature-details {
        flex-wrap: wrap;
        .feature-image {
          width: 100% !important;
        }
      }
	}
	  @media(max-width: 470px) {
	  .premium-video-block {
		.video-bg {
		  width: 100%;
		  margin: 0;
		}
	  }
	}
	@media(max-width: 330px) {
	  .trial-panel {
		.header-block {
		  .header-title {
			line-height: $pricing-title-mobile-line-height;	
		  }
		}
		.header-description {
		  line-height: $pricing-title-mobile-line-height;
		}
	  }
	  .video-block {
		.subscription-view {
		  margin-bottom: -$pricing-table-transform-height-small;
		  transform: translateY(-$pricing-table-transform-height-small);
		  &.without-title {
		   margin-bottom: -$pricing-table-transform-height-large-t;
		   transform: translateY(-$pricing-table-transform-height-large-t);
		  }
		}
	  }
	}
  }
</style>
